import React, { useCallback, useEffect, useMemo } from "react";

import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  GoalBasedFinancialPlanId,
  GoalBasedFinancialPlanSavingsId,
} from "@fartherfinance/frontend/api/FinancialPlans/ids";
import { GoalBasedFinancialPlanIncomeResponse } from "@fartherfinance/frontend/api/FinancialPlans/income/types";
import { GoalBasedFinancialPlanRetirementHorizonResponse } from "@fartherfinance/frontend/api/FinancialPlans/retirementHorizon/types";
import { GoalBasedFinancialPlanSavingsResponse } from "@fartherfinance/frontend/api/FinancialPlans/savings/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import {
  EndsField,
  FromAccountField,
  NameField,
  OwnerField,
  RepeatsField,
  StartsField,
} from "../Shared/constants";
import FormDrawer from "../Shared/FormDrawer/FormDrawer";
import { generateStartAndEndPlanDatesFromForm } from "../Shared/helpers/generateStartAndEndPlanDatesFromForm";
import { getFromAccountFromOptions } from "../Shared/helpers/getFromAccountFromOptions";
import { ClientAndSpouseDetails } from "../Shared/hooks/useGetClientAndSpouseDetails";
import RepeatsMultiDropdownInput from "../Shared/MultiDropdown/Repeats/Repeats";
import { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";
import removeCommasUnd from "@src/utils/CommaHelperFunctions/removeCommasUnd";
import {
  formNumberFieldCustomRegex,
  formNumberFieldRules,
  formNumberFieldWithCommasCustomRegex,
  formNumberFieldWithCommasRules,
} from "@src/utils/formValidation";

import {
  AddedAccountOptionExtended,
  EmployerMatchField,
  FundingSourceField,
  SavingsMutations,
} from "./Types";

import type {
  InitialPlanDateFieldValuesAndOwnerField,
  OwnerFieldType,
  RepeatDropdownFormFieldTypes,
} from "../Shared/Types";

const FundingSourceField = "FundingSourceField";
const SavingsContributionField = "SavingsContributionField";
const EmployerMatchField = "EmployerMatchField";
const EmployerMatchAmountField = "EmployerMatchAmountField";
const EmployerMatchPercentageField = "EmployerMatchPercentageField";

const EmployerMatchOptions: EmployerMatchField[] = [
  { label: "Yes (Percentage of Salary)", value: "PercentageOfSalary" },
  { label: "Yes (Dollar Amount)", value: "DollarAmount" },
  { label: "No", value: "No" },
];

type SavingsFormFieldTypes = {
  [OwnerField]: OwnerFieldType;
  [NameField]: string;
  [FromAccountField]: AddedAccountOptionExtended | undefined | null;
  [FundingSourceField]: FundingSourceField | undefined;
  [SavingsContributionField]: string | undefined;
  [EmployerMatchField]: EmployerMatchField;
  [EmployerMatchAmountField]: string | undefined;
  [EmployerMatchPercentageField]: string | undefined;
};

type Form = SavingsFormFieldTypes & RepeatDropdownFormFieldTypes;

interface Props {
  drawerOpen: boolean;
  onClose: () => void;
  mutations: SavingsMutations;
  goalBasedFinancialPlanSavingsId: GoalBasedFinancialPlanSavingsId | undefined;
  existingSavingsElement: GoalBasedFinancialPlanSavingsResponse | undefined;
  existingIncomeElements: GoalBasedFinancialPlanIncomeResponse[];
  retirementHorizons:
    | GoalBasedFinancialPlanRetirementHorizonResponse[]
    | undefined;
  initialPlanDateAndOwnerFields: InitialPlanDateFieldValuesAndOwnerField;
  addedAccountOptions: AddedAccountOptionExtended[];
  defaultAccount: AddedAccountOptionExtended | undefined;
  hideSpouseOptions: boolean;
  ownersOptions: OwnerFieldType[];
  clientSpouseData: ClientAndSpouseDetails;
}

const SavingsForm: React.FC<Props> = ({
  drawerOpen,
  onClose,
  mutations,
  goalBasedFinancialPlanSavingsId,
  existingSavingsElement,
  existingIncomeElements,
  retirementHorizons,
  initialPlanDateAndOwnerFields,
  addedAccountOptions,
  defaultAccount,
  hideSpouseOptions,
  ownersOptions,
  clientSpouseData,
}) => {
  const { clientId, goalBasedFinancialPlanId } = useParams<{
    clientId: ClientId;
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  }>();

  const notify = useStatusNotification();

  const { client, spouse } = clientSpouseData;

  const fundingSourceOptions = useMemo(() => {
    return existingIncomeElements.map((inc) => {
      const ownerName =
        inc.owner === clientId ? client.fullName : spouse?.fullName ?? "Spouse";

      return {
        label: `${inc.incomeType} (${ownerName})`,
        value: inc.incomeId,
      };
    });
  }, [clientId, existingIncomeElements, client, spouse]);

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      [NameField]: existingSavingsElement?.name,
      [FromAccountField]:
        getFromAccountFromOptions(
          defaultAccount
            ? defaultAccount.value
            : existingSavingsElement?.account,
          addedAccountOptions
        ) ?? null,
      [FundingSourceField]: fundingSourceOptions.find(
        (el) => el.value === existingSavingsElement?.fundingSource
      ),
      [SavingsContributionField]: existingSavingsElement?.contribution,
      [EmployerMatchField]: EmployerMatchOptions.find(
        (el) => el.value === existingSavingsElement?.employerMatchType
      ) ?? { label: "No", value: "No" },
      [EmployerMatchAmountField]:
        existingSavingsElement?.employerMatchType === "DollarAmount"
          ? existingSavingsElement?.employerMatchValue
          : undefined,
      [EmployerMatchPercentageField]:
        existingSavingsElement?.employerMatchType === "PercentageOfSalary"
          ? existingSavingsElement?.employerMatchValue
          : undefined,
      ...initialPlanDateAndOwnerFields,
    },
  });

  const {
    formState: { isValid },
    getValues,
    reset,
    watch,
  } = methods;
  const employerMatch = watch(EmployerMatchField);
  const repeatsValue = watch(RepeatsField);
  const startsValue = watch(StartsField);
  const endsValue = watch(EndsField);

  const setFormFields = useCallback(() => {
    if (drawerOpen) {
      reset({
        [NameField]: existingSavingsElement?.name,
        [FromAccountField]:
          getFromAccountFromOptions(
            defaultAccount
              ? defaultAccount.value
              : existingSavingsElement?.account,
            addedAccountOptions
          ) ?? null,
        [FundingSourceField]: fundingSourceOptions.find(
          (el) => el.value === existingSavingsElement?.fundingSource
        ),
        [SavingsContributionField]: existingSavingsElement?.contribution,
        [EmployerMatchField]: EmployerMatchOptions.find(
          (el) => el.value === existingSavingsElement?.employerMatchType
        ) ?? { label: "No", value: "No" },
        [EmployerMatchAmountField]:
          existingSavingsElement?.employerMatchType === "DollarAmount"
            ? existingSavingsElement?.employerMatchValue
            : undefined,
        [EmployerMatchPercentageField]:
          existingSavingsElement?.employerMatchType === "PercentageOfSalary"
            ? existingSavingsElement?.employerMatchValue
            : undefined,
        ...initialPlanDateAndOwnerFields,
      });
    }
  }, [
    drawerOpen,
    reset,
    initialPlanDateAndOwnerFields,
    existingSavingsElement,
    addedAccountOptions,
    fundingSourceOptions,
    defaultAccount,
  ]);

  useEffect(() => {
    setFormFields();
  }, [setFormFields]);

  const handleOnClose = () => {
    setFormFields();
    onClose();
  };

  const onSave = async () => {
    const values = getValues();

    if (
      values[NameField] === undefined ||
      values[SavingsContributionField] === undefined ||
      values[FromAccountField] === undefined ||
      values[FromAccountField] === null ||
      values[FundingSourceField] === undefined
    ) {
      return;
    }

    let matchValue = undefined;
    if (values[EmployerMatchField].value === "DollarAmount") {
      matchValue = values[EmployerMatchAmountField];
    } else if (values[EmployerMatchField].value === "PercentageOfSalary") {
      matchValue = values[EmployerMatchPercentageField];
    }

    if (retirementHorizons === undefined) {
      throw new Error("retirementHorizons is undefined");
    }

    const [startDate, endDate] = generateStartAndEndPlanDatesFromForm({
      clientId,
      endPlanDateId:
        existingSavingsElement !== undefined
          ? existingSavingsElement.endDate?.planDateId
          : undefined,
      retirementHorizons,
      startPlanDateId:
        existingSavingsElement !== undefined
          ? existingSavingsElement.startDate?.planDateId
          : undefined,
      form: values,
    });

    if (goalBasedFinancialPlanSavingsId !== undefined) {
      if (existingSavingsElement === undefined) {
        return;
      }

      try {
        await mutations.onUpdateSavings({
          goalBasedFinancialPlanId,
          goalBasedFinancialPlanSavingsId,
          body: {
            owner: values[OwnerField].value,
            name: values[NameField],
            account: values[FromAccountField]?.value,
            fundingSource: values[FundingSourceField].value,
            contribution: removeCommas(values[SavingsContributionField]),
            employerMatchType: values[EmployerMatchField].value,
            employerMatchValue: removeCommasUnd(matchValue),
            startDate,
            endDate,
            repeats: values[RepeatsField].value,
          },
        });

        notify("Savings added", "Success");

        handleOnClose();
      } catch (error) {
        if (isAxiosError<ErrorMessage>(error)) {
          const message = error.response?.data.message;
          notify(`Error adding savings: ${message ?? ""}`, "Error");
        } else {
          notify("Error adding savings", "Error");
        }
      }
    } else {
      try {
        await mutations.onAddSavings({
          goalBasedFinancialPlanId,
          body: {
            owner: values[OwnerField].value,
            name: values[NameField],
            account: values[FromAccountField].value,
            fundingSource: values[FundingSourceField].value,
            contribution: removeCommas(values[SavingsContributionField]),
            employerMatchType: values[EmployerMatchField].value,
            employerMatchValue: removeCommasUnd(matchValue),
            startDate,
            endDate,
            repeats: values[RepeatsField].value,
          },
        });

        notify("Savings added", "Success");

        handleOnClose();
      } catch (error) {
        if (isAxiosError<ErrorMessage>(error)) {
          const message = error.response?.data.message;
          notify(`Error adding savings: ${message ?? ""}`, "Error");
        } else {
          notify("Error adding savings", "Error");
        }
      }
    }
  };

  const onRemove = async () => {
    if (goalBasedFinancialPlanSavingsId === undefined) {
      return;
    }

    try {
      await mutations.onDeleteSavings({
        goalBasedFinancialPlanId,
        goalBasedFinancialPlanSavingsId,
      });

      notify("Savings removed", "Success");
      handleOnClose();
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error removing savings: ${message ?? ""}`, "Error");
      } else {
        notify("Error removing savings", "Error");
      }
    }
  };

  return (
    <FormDrawer
      title={"Savings"}
      drawerOpen={drawerOpen}
      onClose={handleOnClose}
      saveDisabled={mutations.isMutating || !isValid}
      onSave={onSave}
      removeDisabled={mutations.isMutating}
      onRemove={existingSavingsElement ? onRemove : undefined}
      isLoading={mutations.isMutating}
      hasError={false}
      deleteModalTitle={"Remove Savings"}
      deleteModalSubTitle={"Are you sure you want to remove this savings?"}
    >
      <FormProvider {...methods}>
        <FlexWrapper>
          <FormDropdownField
            name={OwnerField}
            label={"Owner"}
            values={ownersOptions}
            disabled={existingSavingsElement !== undefined}
            disableSearch={true}
            required={"Must not be empty"}
          />

          <FormTextField
            name={NameField}
            label="Name"
            required={"Must not be empty"}
          />
        </FlexWrapper>

        <FlexWrapper>
          <FormDropdownField
            name={FromAccountField}
            label="Account"
            values={addedAccountOptions}
            disableSearch={true}
            required={"Must not be empty"}
            disabled={
              existingSavingsElement !== undefined ||
              defaultAccount !== undefined
            }
          />

          <FormDropdownField
            name={FundingSourceField}
            label="Funding Source"
            values={fundingSourceOptions}
            disableSearch={true}
            required={"Must not be empty"}
          />
        </FlexWrapper>

        <FlexWrapper>
          <FormNumberField
            name={SavingsContributionField}
            label="Savings Contribution"
            startAdornment={"$"}
            customRegex={formNumberFieldWithCommasCustomRegex}
            rules={formNumberFieldWithCommasRules({
              required: true,
              minValue: 0,
              maxValue: 100_000_000,
            })}
            autoAddCommas={true}
          />

          <FormDropdownField
            name={EmployerMatchField}
            label="Employer Match"
            values={EmployerMatchOptions}
            disableSearch={true}
            required={"Must not be empty"}
          />
        </FlexWrapper>

        {employerMatch.value === "DollarAmount" && (
          <FlexWrapper>
            <FormNumberField
              name={EmployerMatchAmountField}
              label={"Employer Match Amount"}
              endAdornment={"$"}
              customRegex={formNumberFieldWithCommasCustomRegex}
              rules={formNumberFieldWithCommasRules({
                required: true,
                minValue: 0,
                maxValue: 100_000_000,
              })}
              autoAddCommas={true}
            />
          </FlexWrapper>
        )}

        {employerMatch.value === "PercentageOfSalary" && (
          <FlexWrapper>
            <FormNumberField
              name={EmployerMatchPercentageField}
              label={"Employer Match Percentage"}
              endAdornment={"%"}
              customRegex={formNumberFieldCustomRegex}
              rules={formNumberFieldRules({
                required: true,
                minValue: 0,
                maxValue: 1000,
              })}
            />
          </FlexWrapper>
        )}

        <FlexWrapper>
          <RepeatsMultiDropdownInput
            curRepeatsOption={repeatsValue.value}
            curPlanDateStartOption={startsValue.value}
            curPlanDateEndOption={endsValue.value}
            hideSpouseOptions={hideSpouseOptions}
          />
        </FlexWrapper>
      </FormProvider>
    </FormDrawer>
  );
};

export default SavingsForm;
